<template>
  <header class="global-header">
    <router-link to="/" class="header-logo-link">
      <img src="/lamusica-logo.svg" alt="LaMusica" class="header-logo">
    </router-link>
    
    <div class="header-right">
      <!-- Navigation Tabs -->
      <nav class="header-nav">
        <router-link to="/" class="nav-tab" :class="{ active: isStationsActive }">Stations</router-link>
        <router-link to="/compare" class="nav-tab" :class="{ active: $route.path === '/compare' }">Compare</router-link>
      </nav>
      
      <!-- User Menu -->
      <div class="user-menu-wrapper" ref="userMenuWrapper">
        <button class="btn-user-menu" @click.stop="toggleUserMenu">
          <span class="user-avatar">{{ userInitials }}</span>
          <AppIcon name="chevron-down" />
        </button>
        <div v-if="showUserMenu" class="user-dropdown">
          <div class="user-dropdown-header">
            <div class="user-name">{{ user?.name }}</div>
            <div class="user-email">{{ user?.email }}</div>
          </div>
          <div class="user-dropdown-divider"></div>
          <router-link v-if="isAdmin" to="/admin/users" class="user-dropdown-item" @click="showUserMenu = false">
            <AppIcon name="users" />User Management
          </router-link>
          <router-link v-if="isAdmin" to="/station-groups" class="user-dropdown-item" @click="showUserMenu = false">
            <AppIcon name="layer-group" />Groups
          </router-link>
          <router-link v-if="isAdmin" to="/settings" class="user-dropdown-item" @click="showUserMenu = false">
            <AppIcon name="cog" />Settings
          </router-link>
          <router-link to="/change-password" class="user-dropdown-item" @click="showUserMenu = false">
            <AppIcon name="key" />Change Password
          </router-link>
          <div class="user-dropdown-divider"></div>
          <button class="user-dropdown-item logout" @click="handleLogout">
            <AppIcon name="sign-out-alt" />Sign Out
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'GlobalHeader',
  
  data() {
    return {
      showUserMenu: false
    }
  },
  
  computed: {
    ...mapGetters('auth', ['user', 'isAdmin']),
    
    userInitials() {
      if (!this.user?.name) return '?'
      return this.user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    },
    
    isStationsActive() {
      const path = this.$route.path
      return path === '/' || path === '/stations' || path.startsWith('/station/')
    }
  },
  
  mounted() {
    document.addEventListener('click', this.closeUserMenu)
  },
  
  beforeUnmount() {
    document.removeEventListener('click', this.closeUserMenu)
  },
  
  methods: {
    ...mapActions('auth', ['logout']),
    
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    
    closeUserMenu(e) {
      if (!e.target.closest('.user-menu-wrapper')) {
        this.showUserMenu = false
      }
    },
    
    async handleLogout() {
      await this.logout()
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.global-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
  height: 74px;
  background: var(--white);
  border-bottom: 0.7px solid var(--gray-200);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

.header-logo-link {
  display: flex;
  align-items: center;
}

.header-logo {
  height: 36px;
  width: auto;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-nav {
  display: flex;
  gap: 4px;
  margin-right: 8px;
}

.nav-tab {
  padding: 8px 16px;
  color: var(--gray-500);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.nav-tab:hover {
  color: var(--gray-700);
  background: var(--gray-100);
}

.nav-tab.active,
.nav-tab.router-link-exact-active {
  color: var(--accent);
  background: rgba(51, 147, 255, 0.1);
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  color: var(--gray-500);
  border-radius: 6px;
  transition: all 0.15s ease;
  text-decoration: none;
  font-size: 16px;
}

.icon-btn:hover {
  color: var(--gray-700);
  background: var(--gray-100);
}

.icon-btn.active {
  color: var(--accent);
  background: rgba(51, 147, 255, 0.1);
}

.user-menu-wrapper {
  position: relative;
  margin-left: 8px;
}

.btn-user-menu {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px 4px 4px;
  background: transparent;
  border: 1px solid var(--gray-200);
  border-radius: 50px;
  cursor: pointer;
  color: var(--gray-500);
  transition: all 0.15s ease;
}

.btn-user-menu:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}

.chevron {
  font-size: 10px;
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 220px;
  background: var(--white);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  z-index: 1001;
}

.user-dropdown-header {
  padding: 12px 16px;
  background: var(--gray-50);
}

.user-name {
  font-weight: 600;
  color: var(--gray-900);
  font-size: 14px;
}

.user-email {
  font-size: 12px;
  color: var(--gray-500);
  margin-top: 2px;
}

.user-dropdown-divider {
  height: 1px;
  background: var(--gray-200);
}

.user-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: 14px;
  color: var(--gray-700);
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease;
}

.user-dropdown-item:hover {
  background: var(--gray-50);
}

.user-dropdown-item.logout {
  color: var(--error, #dc2626);
}

.user-dropdown-item svg {
  width: 16px;
  text-align: center;
  font-size: 13px;
}
</style>
